import React, { useState, useRef, useEffect, useCallback } from 'react';
import * as icons from './icons';
import styles from './RichTextEditor.module.css';

interface LinkControlsProps {
  config?: Record<string, unknown>;
  currentState?: { link?: { target: string; title: string; targetOption: string }; selectionText?: string };
  expanded?: boolean;
  onChange?: (action: string, title?: string, target?: string, targetOption?: string) => void;
  onExpandEvent?: () => void;
  doExpand?: () => void;
  doCollapse?: () => void;
  translations?: Record<string, string>;
}

const LinkControls: React.FC<LinkControlsProps> = ({ currentState, onChange }) => {
  const [showModal, setShowModal] = useState(false);
  const [url, setUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const modalRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLDivElement>(null);

  // Close modal on outside click
  useEffect(() => {
    if (!showModal) return;
    const handleClick = (e: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(e.target as Node) &&
        btnRef.current &&
        !btnRef.current.contains(e.target as Node)
      ) {
        closeModal();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showModal]);

  const closeModal = useCallback(() => {
    setShowModal(false);
    setUrl('');
    setLinkText('');
  }, []);

  const handleLinkClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault(); // Prevent editor from losing focus

    if (showModal) {
      closeModal();
      return;
    }

    const selText = currentState?.selectionText || '';
    const existingLink = currentState?.link;

    setLinkText(existingLink?.title || selText || '');
    setUrl(existingLink?.target || '');
    setShowModal(true);
  }, [showModal, closeModal, currentState]);

  const addLink = useCallback(() => {
    if (!onChange || !url) return;

    const finalUrl = url.startsWith('http') ? url : `https://${url}`;
    const title = linkText || finalUrl;

    onChange('link', title, finalUrl, '_blank');
    closeModal();
  }, [onChange, url, linkText, closeModal]);

  const removeLink = useCallback((e: React.MouseEvent) => {
    e.preventDefault(); // Prevent editor from losing focus
    if (!onChange) return;
    onChange('unlink');
  }, [onChange]);

  const existingLink = currentState?.link;
  const hasSelectionText = !!(currentState?.selectionText);

  return (
    <div className={styles.linkControlsWrapper}>
      {/* Link button */}
      <div
        ref={btnRef}
        className={`rdw-option-wrapper ${styles.toolbarBtn} ${existingLink ? styles.toolbarBtnActive : ''}`}
        onMouseDown={handleLinkClick}
        title="Link"
      >
        <img src={icons.link} alt="Link" />
      </div>

      {/* Unlink button */}
      <div
        className={`rdw-option-wrapper ${styles.toolbarBtn}`}
        onMouseDown={removeLink}
        title="Unlink"
      >
        <img src={icons.unlink} alt="Unlink" />
      </div>

      {/* Link modal */}
      {showModal && (
        <div ref={modalRef} className={styles.linkModal} onMouseDown={(e) => e.stopPropagation()}>
          <label className={styles.linkModalLabel}>
            Link Title
            <input
              className={styles.linkModalInput}
              type="text"
              value={linkText}
              onChange={(e) => setLinkText(e.target.value)}
              placeholder="Link text"
              disabled={hasSelectionText}
            />
          </label>
          <label className={styles.linkModalLabel}>
            Link URL
            <input
              className={styles.linkModalInput}
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addLink();
                }
              }}
              autoFocus
            />
          </label>
          <div className={styles.linkModalButtons}>
            <button
              className={styles.linkModalBtn}
              onClick={addLink}
              disabled={!url || (!linkText && !hasSelectionText)}
            >
              Add
            </button>
            <button
              className={styles.linkModalBtnCancel}
              onClick={closeModal}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LinkControls;
